import { ExperienceProductDto, S3FileLocationDto } from "@intelligems/ig-types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { testProductsFromGqlId } from "@src/api/formatters/products";

import {
  IExperienceProductsState,
  IExperienceProductUploadStatus,
} from "./types";

const initialState: IExperienceProductsState = {
  isSuccess: false,
  experienceProducts: [],
  storedExperienceProducts: [],
  isFetching: false,
  isLoading: false,
};

const slice = createSlice({
  name: "experienceProducts",
  initialState,
  reducers: {
    resetExperienceProducts: () => initialState,
    modResetExperienceProducts: (draft) => {
      draft.experienceProducts = [];
    },
    initExperienceProducts: (
      draft,
      action: PayloadAction<
        Pick<
          IExperienceProductsState,
          "isSuccess" | "isLoading" | "isFetching" | "experienceProducts"
        >
      >
    ) => {
      const { isSuccess, isLoading, isFetching, experienceProducts } =
        action.payload;

      draft.isSuccess = isSuccess;
      draft.isLoading = isLoading;
      draft.isFetching = isFetching;
      if (isSuccess && !isLoading && !isFetching) {
        draft.experienceProducts = experienceProducts;
        draft.storedExperienceProducts = experienceProducts;
      }
    },
    initProductsFeFromDto: (
      draft,
      action: PayloadAction<{
        products: ExperienceProductDto[];
        reformatProductIds?: boolean;
      }>
    ) => {
      if (!action.payload.products) return;

      draft.isSuccess = true;
      let tempProducts = action.payload.products;
      if (action.payload.reformatProductIds) {
        tempProducts = testProductsFromGqlId(action.payload.products);
      }
      draft.experienceProducts = tempProducts;
      draft.storedExperienceProducts = tempProducts;
    },
    setExperienceProductsUploadStatus: (
      draft,
      action: PayloadAction<IExperienceProductUploadStatus>
    ) => {
      draft.uploadStatus = action.payload;
    },
    setExperienceProductsLocation: (
      draft,
      action: PayloadAction<S3FileLocationDto>
    ) => {
      draft.productsLocation = action.payload;
      draft.uploadStatus = "uploadDone";
    },
    processSavedExperienceProducts: (draft) => {
      draft.storedExperienceProducts = draft.experienceProducts;
      draft.productsLocation = undefined;
      draft.uploadStatus = undefined;
    },
  },
  selectors: {
    getExperienceProducts: (state) => state.experienceProducts,
    getStoredExperienceProducts: (state) => state.storedExperienceProducts,
    areExperienceProductsLoading: (state) => state.isLoading,
    areExperienceProductsLoaded: (state) => state.isSuccess,
    selectedExperienceProductsState: (state) => state,
    areExperienceProductsFetching: (state) => state.isFetching,
    getExperienceProductsCount: (state) => state.experienceProducts.length,
    getExperienceProductsLocation: (state) => state.productsLocation,
    getExperienceProductUploadStatus: (state) => state.uploadStatus,
  },
});

export const {
  initExperienceProducts,
  resetExperienceProducts,
  setExperienceProductsLocation,
  setExperienceProductsUploadStatus,
  initProductsFeFromDto,
  modResetExperienceProducts,
  processSavedExperienceProducts,
} = slice.actions;

export const experienceProductsReducer = slice.reducer;
export const {
  getExperienceProducts,
  getStoredExperienceProducts,
  areExperienceProductsLoading,
  selectedExperienceProductsState,
  areExperienceProductsLoaded,
  areExperienceProductsFetching,
  getExperienceProductsCount,
  getExperienceProductsLocation,
  getExperienceProductUploadStatus,
} = slice.selectors;
